import { FINANCE_CONSTANTS } from "@constants/carFinance";
import getIndianCurrencyFormat from "@helpers/getIndianCurrencyFormat";
import getMonthlyEMI from "@helpers/getMonthlyEMI";
import type {
  SavingsBreakupData,
  BankData,
  FinanceSlab,
} from "@interfaces/CarFinance.types";

export const getRIPPlusConsumer = (
  savingsBreakup: SavingsBreakupData
): number => {
  return (
    (savingsBreakup?.ripdiscount || 0) + (savingsBreakup?.consumerdiscount || 0)
  );
};

export const convertAmountToNumber = (val: string): number => {
  try {
    if (!val || typeof val !== "string") return 0;
    const cleaned = val.replace(/[^\d]/g, "");
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
  } catch (error) {
    console.error("Error converting amount to number:", error);
    return 0;
  }
};

export const getCarLoanDiscount = (
  financeSlabData: FinanceSlab[],
  currentDownPayment: number,
  currentCarLoanDiscount: number,
  onRoadPrice: number,
  savingsBreakupData: SavingsBreakupData,
  maxSavings: number
): number => {
  try {
    if (!financeSlabData || !Array.isArray(financeSlabData)) {
      return Number(maxSavings) || 0;
    }

    const val =
      Number(onRoadPrice || 0) -
      getRIPPlusConsumer(savingsBreakupData) -
      currentCarLoanDiscount -
      (savingsBreakupData?.ackodiscount || 0) -
      Number(currentDownPayment || 0);

    if (val <= 0) {
      return 0;
    }

    const discountValueSlab = financeSlabData.find((financeSlab) => {
      return financeSlab?.max_loan_amount >= val;
    });

    if (!discountValueSlab) {
      return Number(maxSavings) || 0;
    }

    return Number(discountValueSlab.saving_amount) || 0;
  } catch (error) {
    console.error("Error calculating car loan discount:", error);
    return 0;
  }
};

export const calculateADPrice = (
  onRoadPrice: number,
  savingsBreakupData: SavingsBreakupData,
  carLoanDiscount: number
): number => {
  try {
    return Math.max(
      0,
      Number(onRoadPrice || 0) -
        getRIPPlusConsumer(savingsBreakupData) -
        carLoanDiscount -
        (savingsBreakupData?.ackodiscount || 0) -
        Number(savingsBreakupData?.newyear_discount || 0)
    );
  } catch (error) {
    console.error("Error calculating AD price:", error);
    return Number(onRoadPrice || 0);
  }
};

export const calculateCarValue = (
  onRoadPrice: number,
  savingsBreakupData: SavingsBreakupData
): number => {
  try {
    return Math.max(
      0,
      Number(onRoadPrice || 0) -
        (savingsBreakupData?.ackodiscount || 0) -
        Number(savingsBreakupData?.newyear_discount || 0)
    );
  } catch (error) {
    console.error("Error calculating car value:", error);
    return Number(onRoadPrice || 0);
  }
};

export const calculateMinDownPayment = (carValue: number): number => {
  return carValue * FINANCE_CONSTANTS.MIN_DOWN_PAYMENT_PERCENTAGE;
};

export const calculateMaxDownPayment = (carValue: number): number => {
  return carValue * FINANCE_CONSTANTS.MAX_DOWN_PAYMENT_PERCENTAGE;
};

export const getTotalEmiSavings = (
  onRoadPrice: number,
  savingsBreakupData: SavingsBreakupData,
  downPayment: number,
  ADPrice: number,
  otherInterest: number | string,
  initialSelectedBank: BankData | null,
  loanTenure: number
): string => {
  try {
    const hypothecationCharges = Number(
      FINANCE_CONSTANTS.HYPOTHECATION_CHARGES || 0
    );
    const dealerLoanAmount = Math.max(
      0,
      Number(onRoadPrice || 0) -
        getRIPPlusConsumer(savingsBreakupData) -
        Number(downPayment || 0) +
        hypothecationCharges
    );
    const adLoanAmount = Math.max(
      0,
      ADPrice - Number(downPayment || 0) + hypothecationCharges
    );

    const dealerInterest = Number(otherInterest || 0);
    const adInterest = Number(initialSelectedBank?.interest_rate || 0);

    if (dealerInterest <= 0 || adInterest <= 0 || loanTenure <= 0) {
      return "-";
    }

    const dealerMonthlyEMI = convertAmountToNumber(
      getMonthlyEMI(dealerLoanAmount, dealerInterest, loanTenure)
    );
    const adMonthlyEMI = convertAmountToNumber(
      getMonthlyEMI(adLoanAmount, adInterest, loanTenure)
    );

    const emiSavings = (dealerMonthlyEMI - adMonthlyEMI) * loanTenure * FINANCE_CONSTANTS.MONTHS;
    if (emiSavings < 0) {
      return "-";
    }
    return `₹${getIndianCurrencyFormat(Math.abs(emiSavings))}`;
  } catch (error) {
    console.error("Error calculating EMI savings:", error);
    return "-";
  }
};

export const calculateTotalAckoDriveSavings = (
  savingsBreakupData: SavingsBreakupData,
  carLoanDiscount: number,
  emiSavingsStr: string
): string => {
  try {
    const emiSavingsNum =
      emiSavingsStr === "-" ? 0 : convertAmountToNumber(emiSavingsStr);

    return getIndianCurrencyFormat(
      (savingsBreakupData?.ackodiscount || 0) +
        Number(savingsBreakupData?.newyear_discount || 0) +
        carLoanDiscount +
        emiSavingsNum
    );
  } catch (error) {
    console.error("Error calculating total savings:", error);
    return "0";
  }
};

export const parseInterest = (val: any): number | string => {
  if (val === null || val === undefined || val === "") return "";
  const num = parseFloat(val);
  return isNaN(num) ? "" : num;
};

export const getActiveBanks = (carFinanceBankData: BankData[]): BankData[] => {
  return carFinanceBankData.filter((bank) => bank.is_active) || [];
};

export const getInitialSelectedBank = (
  bankArray: BankData[],
  selectedBankValue: BankData
): BankData | null => {
  return (
    bankArray.find(
      (bank) => parseFloat(bank.id) === parseFloat(selectedBankValue?.id)
    ) ||
    bankArray[0] ||
    null
  );
};

export const calculateDefaultDownPayment = (
  onRoadPrice: number,
  savingsBreakupData: SavingsBreakupData
): number => {
  return (
    (Number(onRoadPrice || 0) -
      (savingsBreakupData?.ripdiscount || 0) -
      (savingsBreakupData?.consumerdiscount || 0) -
      Number(savingsBreakupData?.ackodiscount || 0) -
      Number(savingsBreakupData?.newyear_discount || 0)) *
    FINANCE_CONSTANTS.DEFAULT_DOWN_PAYMENT_PERCENTAGE
  );
};
